import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Download } from 'lucide-react';
import { wbsCodes } from '../dummyData';
import { downloadCSV, downloadXLSX, formatSAPDate, formatReadableDate } from '../utils/exportUtils';
import { formatNumber } from '../utils/formatUtils';

const Approvals = ({ timesheets = [], users = [], onAction }) => {
  const pendingApprovals = timesheets
    .filter(ts => ts.status === 'Pending')
    .map(ts => ({
      ...ts,
      user: users.find(u => u.id === ts.userId),
      wbs: wbsCodes.find(w => w.code === ts.wbsCode)
    }));

  const handleAction = (id, action) => {
    // Call the parent handler
    onAction(id, action);
  };

  const exportApprovedTimesheets = () => {
    const approved = timesheets.filter(ts => ts.status === 'Approved');
    const headers = ['PERNR', 'WORKDATE', 'LSTAR', 'SKOSTL', 'SE_WBS_ELEMENT', 'CATSAMOUNT'];
    const rows = approved.map(ts => {
      const user = users.find(u => u.id === ts.userId);
      return [
        user?.personnelNumber || '',
        formatSAPDate(ts.date),
        user?.roleScope || '',
        user?.costCenter || '',
        ts.wbsCode,
        ts.hours
      ];
    });
    downloadCSV(headers, rows, `SAP_Approved_CATS_${formatSAPDate(new Date())}.csv`, false);
  };

  const exportToExcel = () => {
    const headers = ['Employee', 'Date', 'WBS Code', 'Description', 'Hours', 'Status'];
    const rows = timesheets.map(ts => {
      const user = users.find(u => u.id === ts.userId);
      const wbs = wbsCodes.find(w => w.code === ts.wbsCode);
      return [
        user?.name || '',
        formatReadableDate(ts.date),
        ts.wbsCode,
        wbs?.description || '',
        ts.hours,
        ts.status
      ];
    });
    downloadXLSX(headers, rows, `Timesheet_Approvals_${formatSAPDate(new Date())}.xlsx`, 'Approvals');
  };

  return (
    <div className="max-w-5xl mx-auto h-full flex flex-col">
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Timesheet Approvals</h1>
          <p className="text-slate-500 text-xs sm:text-sm font-medium mt-1">Review and authorize submitted time for your delegated projects.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
          <button 
            onClick={exportToExcel}
            className="flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-sm active:scale-95"
          >
            <Download className="w-4 h-4 text-indigo-500" /> Export Excel
          </button>
          <button 
            onClick={exportApprovedTimesheets}
            className="flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all shadow-sm active:scale-95"
          >
            <Download className="w-4 h-4" /> Export SAP
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {pendingApprovals.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mb-4" />
            <h3 className="text-lg font-black text-slate-800 tracking-tight">All Caught Up!</h3>
            <p className="text-slate-500 text-sm font-medium">There are no pending timesheets requiring your approval at this time.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 text-[11px] font-black uppercase tracking-widest border-b border-slate-200">
                    <th className="px-6 py-4">Employee</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Project WBS</th>
                    <th className="px-6 py-4 text-right">Hours</th>
                    <th className="px-6 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendingApprovals.map(ts => (
                    <tr key={ts.id} className="hover:bg-indigo-50/20 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 font-bold flex items-center justify-center mr-3 flex-shrink-0">
                            {ts.user?.name.charAt(0)}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{ts.user?.name}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{ts.user?.roleScope}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-slate-600">
                        {ts.date}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-mono text-[11px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 inline-flex rounded shadow-sm">{ts.wbsCode}</div>
                        <div className="text-[11px] text-slate-400 font-bold mt-1 uppercase truncate max-w-[200px]">{ts.wbs?.description}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {ts.hours > 8 ? (
                          <span className="flex items-center justify-end text-rose-600 font-bold">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {formatNumber(ts.hours)}
                          </span>
                        ) : (
                          <span className="font-black text-indigo-700 text-lg">{formatNumber(ts.hours)}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center space-x-2">
                           <button 
                             onClick={() => handleAction(ts.id, 'approve')}
                             className="flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
                           >
                             <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve
                           </button>
                           <button 
                             onClick={() => handleAction(ts.id, 'reject')}
                             className="flex items-center px-4 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
                           >
                             <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                           </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100">
              {pendingApprovals.map(ts => (
                <div key={ts.id} className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-lg">
                        {ts.user?.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-black text-slate-800 text-sm">{ts.user?.name}</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">{ts.user?.roleScope}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{ts.date}</p>
                      <p className={`text-xl font-black ${ts.hours > 8 ? 'text-rose-600' : 'text-indigo-700'}`}>
                        {formatNumber(ts.hours)}h
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <p className="font-mono text-xs font-bold text-indigo-600">{ts.wbsCode}</p>
                    <p className="text-[11px] text-slate-500 font-semibold mt-0.5">{ts.wbs?.description}</p>
                  </div>

                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleAction(ts.id, 'approve')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-emerald-100 active:scale-95 transition-all"
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button 
                      onClick={() => handleAction(ts.id, 'reject')}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-white text-rose-600 border border-rose-100 rounded-xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Approvals;
